// Central game-state store. All layers read from here; mutations go through set()/update() and notify subscribers.
import { bus } from './bus.js';

const initialState = () => ({
  screen: 'menu',            // 'menu' | 'loading' | 'game'
  zooName: '',
  mapSize: 'medium',         // 'small' | 'medium' | 'large'
  // Economy
  money: 75000,
  loan: 0,
  // Meters
  reputation: 50,            // 0-100
  prestige: 0,               // grows with media events, decays slowly
  visitorCount: 0,
  entranceFee: 25,
  zooClosed: false,
  loanInterest: 0.001,
  staff_keeper: 0,
  staff_cleaner: 0,
  staff_guide: 0,
  // Government animal mandate — total ADULT cap, scaled to species count, raised via permit tier (1..5).
  // Derived from permitTier at runtime (economy ctor / applySave); this default is just the pre-init value.
  animalCap: 76,
  permitTier: 1,
  adultCount: 0,
  mandateOffer: null,
  // Per-building-type service management (zoo-wide, grouped by category)
  catStaff: { food: 0, drink: 0, fun: 0, facility: 0 },
  catTier: { food: 1, drink: 1, fun: 1, facility: 1 },   // 0 Budget · 1 Standard · 2 Premium
  catInfo: {},
  // Time
  day: 1,
  month: 3,                  // start in spring (March)
  year: 1,
  season: 'spring',
  speed: 1,                  // 0 = paused, 1/2/3
  weather: null,             // null | 'heatwave' | 'storm'
  // Tools
  activeTool: null,          // 'terraform-raise' | 'terraform-lower' | 'terraform-flatten' | 'path' | 'fence' | ...
  overlayLens: null,         // active Insight lens: null | 'happiness'|'demand'|'wellbeing'|'dna'|'money'|'service'|'traffic'|'health'
  selectedEnc: null,         // enclosure id shown in the Habitat panel
  selBuilding: null,         // {cx,cz} of the building shown in the Building panel
  inspection: null,          // last welfare inspection { grade, avg, cash, day }
  released: 0,               // animals released to the wild (conservation)
  sickTotal: 0,              // sick animals across the zoo (drives the topbar health alert)
  projects: {},              // funded grand projects (late-game money sink) — { id: true }
  openPanel: null,           // which side panel is open: 'finance'|'save'|'visitors'|'animals'|'book'|'tv'|'overlays'|'terrain'|'infra'|'shop-animals'|'shop-buildings'
  // Finance ledger (today so far + last full day) and zoo-wide service quality (0..1)
  finance: { admission: 0, shops: 0, contracts: 0, wages: 0, upkeep: 0, interest: 0, permit: 0 },
  financePrev: { admission: 0, shops: 0, contracts: 0, wages: 0, upkeep: 0, interest: 0, permit: 0 },
  serviceQuality: 1,
  graphics: { auto: true, quality: 2, treeDensity: 0.3, fpsCap: 30, shadowRes: 128 },
  newsFeed: [],
});

let state = initialState();
const subs = new Set();
let notifyQueued = false;

// Batched notify: many store.set() calls in one tick (sales, sim updates, events)
// collapse into ONE subscriber notification per microtask — React re-renders once
// instead of hundreds of times per second.
function queueNotify() {
  if (notifyQueued) return;
  notifyQueued = true;
  queueMicrotask(() => {
    notifyQueued = false;
    subs.forEach((fn) => fn(state));
  });
}

export const store = {
  get: () => state,
  set(patch) {
    state = { ...state, ...patch };
    queueNotify();
  },
  update(fn) { this.set(fn(state)); },
  reset() { state = initialState(); queueNotify(); },
  subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
};

export const MAP_SIZES = {
  small:  { label: 'Small',  cells: 160, worldSize: 320 },
  medium: { label: 'Medium', cells: 224, worldSize: 448 },
  large:  { label: 'Large',  cells: 288, worldSize: 576 },
};

export function pushNews(headline, kind = 'info') {
  const item = { id: Date.now() + Math.random(), headline, kind, day: state.day, month: state.month, year: state.year };
  store.set({ newsFeed: [item, ...state.newsFeed].slice(0, 50) });
  bus.emit('news', item);
}
